import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatTooltipModule } from '@angular/material/tooltip'
import { Observable } from 'rxjs'

import { SupplierPortalService } from '../../../core/services/supplier-portal/supplier-portal.service'
import { DocumentTypeService } from '../../../core/services/document/document-type/document-type.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorDocumentoUpload, FornecedorDocumentoVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { downloadBase64File } from '../../../shared/utils/download_base64'
import { DialogConfirmComponent } from '../../../shared/components/dialog-confirm/dialog-confirm.component'
import {
  DocumentTypeOption,
  loadAcceptedDocumentTypes,
  SupplierPortalDocumentScope,
} from '../supplier-portal-document-type.util'

// Componente embutível de gestão de documentos — usado tanto dentro dos modais de
// edição (motoristaId/veiculoId já existe) quanto na modal standalone (documentos.
// Um único componente serve motorista e veículo — o @Input escopo decide qual par de
// métodos do service é chamado e quais tipos de documento ficam disponíveis no select.
@Component({
  selector: 'supplier-portal-documents',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormField, MatInput, MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinner,
    MatProgressBar,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './supplier-portal-documents.component.html',
  styleUrl: './supplier-portal-documents.component.scss',
})
export class SupplierPortalDocumentsComponent implements OnInit, OnChanges {
  @Input({ required: true }) escopo!: SupplierPortalDocumentScope
  @Input({ required: true }) ownerId!: number | string

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private supplierPortalService: SupplierPortalService,
    private documentTypeService: DocumentTypeService,
    private snackbar: SnackbarCustomService,
  ) {}

  documents: FornecedorDocumentoVm[] = []
  tableSource: MatTableDataSource<FornecedorDocumentoVm> = new MatTableDataSource<FornecedorDocumentoVm>()
  displayedColumns: string[] = ['descricao', 'nomeArquivo', 'tipoDocDescricao', 'dataCriacao', 'action']
  loading = false

  tipoDocOptions: DocumentTypeOption[] = []
  loadingTipos = false

  form!: FormGroup
  selectedFile: File | null = null
  uploading = false
  downloadingId: number | null = null
  deletingId: number | null = null

  ngOnInit(): void {
    this.createForm()
    this.loadDocumentTypes()
    this.loadDocuments()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ownerId'] && !changes['ownerId'].firstChange) {
      this.loadDocuments()
    }
  }

  private createForm(): void {
    this.form = this.fb.group({
      descricao: ['', Validators.required],
      tipoDocId: [null, Validators.required],
      file: ['', Validators.required],
    })
  }

  private loadDocumentTypes(): void {
    this.loadingTipos = true
    loadAcceptedDocumentTypes(this.documentTypeService, this.escopo).subscribe({
      next: (options) => { this.tipoDocOptions = options },
      error: () => { this.tipoDocOptions = [] },
      complete: () => { this.loadingTipos = false },
    })
  }

  loadDocuments(): void {
    this.loading = true
    this.listCall().subscribe({
      next: (res: any) => {
        this.documents = res.data ?? []
        this.tableSource = new MatTableDataSource<FornecedorDocumentoVm>(this.documents)
      },
      error: (err: ErrorRequestModel) => { this.loading = false; this.snackbar.errorRequest(err) },
      complete: () => { this.loading = false },
    })
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0]
      this.form.get('file')?.setValue(this.selectedFile.name)
    } else {
      this.selectedFile = null
      this.form.get('file')?.setValue('')
    }
  }

  upload(): void {
    if (this.form.invalid || !this.selectedFile) {
      this.form.markAllAsTouched()
      return
    }
    this.uploading = true
    const model = new FornecedorDocumentoUpload()
    model.Descricao = this.form.get('descricao')!.value
    model.TipoDocId = this.form.get('tipoDocId')!.value
    model.Arquivo = this.selectedFile

    this.uploadCall(model.toFormData()).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message || 'Documento enviado com sucesso.')
        this.resetForm()
        this.loadDocuments()
      },
      error: (err: ErrorRequestModel) => { this.uploading = false; this.snackbar.errorRequest(err) },
      complete: () => { this.uploading = false },
    })
  }

  private resetForm(): void {
    this.form.reset()
    this.selectedFile = null
  }

  download(doc: FornecedorDocumentoVm): void {
    this.downloadingId = doc.id
    this.downloadCall(doc.id).subscribe({
      next: (res: any) => {
        const payload = res.data ?? res
        downloadBase64File(payload.fileContent, payload.fileName ?? doc.nomeArquivo, payload.type)
      },
      error: (err: ErrorRequestModel) => { this.snackbar.errorRequest(err) },
      complete: () => { this.downloadingId = null },
    })
  }

  confirmDelete(doc: FornecedorDocumentoVm): void {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '400px',
      width: '400px',
      data: {
        title: 'Excluir documento',
        message: `Deseja realmente excluir "${doc.descricao || doc.nomeArquivo}"? Essa ação não pode ser desfeita.`,
        confirmLabel: 'Excluir',
      },
    })
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) this.deleteDocument(doc)
    })
  }

  private deleteDocument(doc: FornecedorDocumentoVm): void {
    this.deletingId = doc.id
    this.deleteCall(doc.id).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message || 'Documento excluído com sucesso.')
        this.loadDocuments()
      },
      error: (err: ErrorRequestModel) => { this.deletingId = null; this.snackbar.errorRequest(err) },
    })
  }

  private listCall(): Observable<any> {
    return this.escopo === 'motorista'
      ? this.supplierPortalService.listMotoristaDocumentos(this.ownerId as number)
      : this.supplierPortalService.listVeiculoDocumentos(this.ownerId as string)
  }

  private uploadCall(formData: FormData): Observable<any> {
    return this.escopo === 'motorista'
      ? this.supplierPortalService.uploadMotoristaDocumento(this.ownerId as number, formData)
      : this.supplierPortalService.uploadVeiculoDocumento(this.ownerId as string, formData)
  }

  private downloadCall(documentoId: number): Observable<any> {
    return this.escopo === 'motorista'
      ? this.supplierPortalService.downloadMotoristaDocumento(this.ownerId as number, documentoId)
      : this.supplierPortalService.downloadVeiculoDocumento(this.ownerId as string, documentoId)
  }

  private deleteCall(documentoId: number): Observable<any> {
    return this.escopo === 'motorista'
      ? this.supplierPortalService.deleteMotoristaDocumento(this.ownerId as number, documentoId)
      : this.supplierPortalService.deleteVeiculoDocumento(this.ownerId as string, documentoId)
  }
}
